import { useState, useRef, useEffect } from 'react'
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps'

interface AddressData {
  formattedAddress: string
  placeId: string
  city?: string
  postcode?: string
  country?: string
  countryCode?: string
  state?: string
  stateCode?: string
}

interface AddressAutocompleteProps {
  value: string
  onChange: (value: string) => void
  onPlaceSelect: (data: AddressData) => void
  placeholder?: string
  disabled?: boolean
  error?: string
  className?: string
}

const AddressAutocompleteInput = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = 'Search for a location...',
  disabled = false,
  error,
  className = ''
}: AddressAutocompleteProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [loadingError, setLoadingError] = useState<string | null>(null)
  const [isInputReady, setIsInputReady] = useState(false)
  const places = useMapsLibrary('places')

  const onChangeRef = useRef(onChange)
  const onPlaceSelectRef = useRef(onPlaceSelect)
  const valueRef = useRef(value)

  useEffect(() => {
    onChangeRef.current = onChange
    onPlaceSelectRef.current = onPlaceSelect
    valueRef.current = value
  }, [onChange, onPlaceSelect, value])

  useEffect(() => {
    if (!places || !containerRef.current) {
      if (import.meta.env.DEV) {
        console.log('[AddressAutocomplete] Waiting for Places library to load...')
      }
      return
    }

    try {
      if (import.meta.env.DEV) {
        console.log('[AddressAutocomplete] Initializing Google PlaceAutocompleteElement...')
      }

      // @ts-expect-error - PlaceAutocompleteElement is a new preview feature and might not be fully typed
      const autocompleteElement = new places.PlaceAutocompleteElement({
        types: ['address']
      })
      
      autocompleteElement.setAttribute('placeholder', placeholder)
      
      // Forward standard input attributes if needed
      if (disabled) {
          autocompleteElement.setAttribute('disabled', 'true')
      }

      // Sync initial text from the parent if available
      if (valueRef.current) {
        ;(autocompleteElement as unknown as { inputValue: string }).inputValue = valueRef.current;
      }
      
      containerRef.current.appendChild(autocompleteElement)
      setLoadingError(null)

      if (import.meta.env.DEV) {
        console.log('[AddressAutocomplete] ✅ Autocomplete initialized successfully')
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const handler = async (e: any) => {
        try {
          const place = e.placePrediction.toPlace()

          if (import.meta.env.DEV) {
            console.log('[AddressAutocomplete] Prediction selected, fetching fields...')
          }

          await place.fetchFields({
            fields: ['formattedAddress', 'id', 'addressComponents']
          })

          if (place.formattedAddress && place.id) {
            let city = ''
            let postcode = ''
            let country = ''
            let countryCode = ''
            let state = ''
            let stateCode = ''

            if (place.addressComponents) {
              for (const component of place.addressComponents) {
                const types = component.types
                if (types.includes('locality')) {
                  city = component.longText
                } else if (types.includes('postal_town')) {
                  city = city || component.longText
                } else if (types.includes('postal_code')) {
                  postcode = component.longText
                } else if (types.includes('country')) {
                  country = component.longText
                  countryCode = component.shortText
                } else if (types.includes('administrative_area_level_1')) {
                  state = component.longText
                  stateCode = component.shortText
                }
              }
            }

            onChangeRef.current(place.formattedAddress)
            onPlaceSelectRef.current({
              formattedAddress: place.formattedAddress,
              placeId: place.id,
              city,
              postcode,
              country,
              countryCode,
              state,
              stateCode
            })
            if (import.meta.env.DEV) {
              console.log('[AddressAutocomplete] ✅ Address data sent to parent:', { city, postcode, country, countryCode, state, stateCode })
            }
          } else if (import.meta.env.DEV) {
            console.warn('[AddressAutocomplete] ⚠️ Incomplete place data fetched:', place)
          }
        } catch (err) {
          if (import.meta.env.DEV) {
            console.error('[AddressAutocomplete] ❌ Error handling place selection:', err)
          }
        }
      }

      autocompleteElement.addEventListener('gmp-select', handler)

      return () => {
        if (import.meta.env.DEV) {
          console.log('[AddressAutocomplete] Cleaning up autocomplete element and listeners')
        }
        autocompleteElement.removeEventListener('gmp-select', handler)
        if (autocompleteElement.parentNode) {
          autocompleteElement.parentNode.removeChild(autocompleteElement)
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      if (import.meta.env.DEV) {
        console.error('[AddressAutocomplete] ❌ Failed to initialize autocomplete array:', errorMessage, err)
      }
      setLoadingError(errorMessage)
    }
  }, [places, placeholder, disabled])

  const handleInputFocus = () => {
    if (!isInputReady) {
      setIsInputReady(true)
    }
  }

  return (
    <div className="space-y-1">
      <style>{`
        gmp-place-autocomplete {
          width: 100%;
          display: block;
          color-scheme: light;
        }
        gmp-place-autocomplete input {
          width: 100%;
          height: 36px;
          border-radius: 10px;
          border: 1px solid ${error || loadingError ? '#ef4444' : '#d1d5db'};
          background-color: ${error || loadingError ? '#fef2f2' : '#ffffff'};
          padding-left: 0.75rem;
          padding-right: 0.75rem;
          outline: none;
          transition: all 0.15s ease-in-out;
          color: #28282a;
        }
        gmp-place-autocomplete input:focus {
          border-color: ${error || loadingError ? '#ef4444' : '#6b7280'};
        }
      `}</style>
      <div 
        ref={containerRef}
        onClick={handleInputFocus}
        className={className}
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
      {loadingError && <p className="text-sm text-orange-600">⚠️ Autocomplete unavailable: {loadingError}</p>}
    </div>
  )
}

const FallbackAddressInput = (props: AddressAutocompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isInputReady, setIsInputReady] = useState(false)

  const handleFocus = () => {
    if (!isInputReady) {
      setIsInputReady(true)
    }
  }

  return (
    <div className="space-y-1">
      <input
        ref={inputRef}
        type="text"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        onFocus={handleFocus}
        readOnly={!isInputReady}
        placeholder={props.placeholder || 'Search for a location...'}
        disabled={props.disabled}
        name="delivery-addr-manual"
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore="true"
        className={`w-full h-[36px] rounded-[10px] border-[1px] px-3 outline-none transition-all ${
          props.error ? 'border-red-500 bg-red-50' : 'border-border focus:border-gray-500'
        } ${props.className || ''}`}
      />
      {props.error && <p className="text-sm text-red-600">{props.error}</p>}
      <p className="text-xs text-muted-foreground">💡 Add Google Maps API key to enable address autocomplete</p>
    </div>
  )
}

export interface AddressAutocompleteWrapperProps extends AddressAutocompleteProps {
  apiKey?: string
}

export const AddressAutocomplete = ({ apiKey, ...props }: AddressAutocompleteWrapperProps) => {
  const mapsApiKey = apiKey || import.meta.env.VITE_GOOGLE_MAPS_API_KEY

  if (import.meta.env.DEV) {
    console.log('[AddressAutocomplete] Component mounted')
    console.log('[AddressAutocomplete] API Key status:', {
      provided: !!apiKey,
      fromEnv: !!import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
      isPlaceholder: mapsApiKey === 'YOUR_GOOGLE_MAPS_API_KEY_HERE'
    })
  }

  if (!mapsApiKey || mapsApiKey === 'YOUR_GOOGLE_MAPS_API_KEY_HERE') {
    if (import.meta.env.DEV) {
      console.warn('[AddressAutocomplete] ⚠️ API key is missing or using placeholder')
      console.warn('[AddressAutocomplete] Falling back to regular text input (no autocomplete)')
    }

    return <FallbackAddressInput {...props} />
  }

  if (import.meta.env.DEV) {
    console.log('[AddressAutocomplete] ✅ API key valid, loading Google Maps...')
  }

  return (
    <APIProvider
      apiKey={mapsApiKey}
      libraries={['places']}
      onLoad={() => import.meta.env.DEV && console.log('[AddressAutocomplete] ✅ Google Maps API loaded successfully')}
    >
      <AddressAutocompleteInput {...props} />
    </APIProvider>
  )
}

export type { AddressData }
